import React, { useEffect, useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Crown, TrendUp, Coins, ChartLineUp } from '@phosphor-icons/react';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import CenterStaffAnalytics from '../components/CenterStaffAnalytics';
import { useAuth } from '../context/AuthContext';

const Kpi = ({ label, value, sub, testid }) => (
  <div className="border border-border rounded-xl p-4" data-testid={testid}>
    <p className="text-xs text-foreground/60 uppercase tracking-wider">{label}</p>
    <p className="text-2xl font-heading font-bold mt-1">{value}</p>
    {sub && <p className="text-[11px] text-foreground/50 mt-0.5">{sub}</p>}
  </div>
);

const fmtIt = (d) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

const defaultFrom = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  return fmtIt(d);
};

const maskDate = (v) => {
  const n = v.replace(/\D/g, '').slice(0, 8);
  if (n.length <= 2) return n;
  if (n.length <= 4) return `${n.slice(0, 2)}/${n.slice(2)}`;
  return `${n.slice(0, 2)}/${n.slice(2, 4)}/${n.slice(4)}`;
};

const isValidIt = (v) => /^\d{2}\/\d{2}\/\d{4}$/.test(v);

export default function ProAnalyticsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromInput, setFromInput] = useState(defaultFrom());
  const [toInput, setToInput] = useState(fmtIt(new Date()));
  const [range, setRange] = useState({ from: defaultFrom(), to: fmtIt(new Date()) });

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (range.from) params.set('date_from', range.from);
    if (range.to) params.set('date_to', range.to);
    api.get(`/api/analytics/professional/leads?${params.toString()}`)
      .then(r => { if (active) setData(r.data); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [range]);

  const apply = () => {
    setRange({
      from: isValidIt(fromInput) ? fromInput : '',
      to: isValidIt(toInput) ? toInput : '',
    });
  };

  const reset = () => {
    const f = defaultFrom();
    const t = fmtIt(new Date());
    setFromInput(f);
    setToInput(t);
    setRange({ from: f, to: t });
  };

  const dateFilter = (
    <div className="flex flex-wrap items-end gap-2" data-testid="pro-analytics-date-filter">
      <div>
        <label className="block text-[11px] text-foreground/60 mb-1">Da</label>
        <input
          value={fromInput}
          onChange={(e) => setFromInput(maskDate(e.target.value))}
          placeholder="gg/mm/aaaa"
          inputMode="numeric"
          data-testid="pro-analytics-date-from"
          className="w-32 px-3 py-1.5 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
      </div>
      <div>
        <label className="block text-[11px] text-foreground/60 mb-1">A</label>
        <input
          value={toInput}
          onChange={(e) => setToInput(maskDate(e.target.value))}
          placeholder="gg/mm/aaaa"
          inputMode="numeric"
          data-testid="pro-analytics-date-to"
          className="w-32 px-3 py-1.5 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
      </div>
      <Button onClick={apply} className="rounded-full h-9 px-4" data-testid="pro-analytics-apply-btn">Applica</Button>
      <Button onClick={reset} variant="outline" className="rounded-full h-9 px-4" data-testid="pro-analytics-reset-btn">Azzera</Button>
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-10 space-y-6" data-testid="pro-analytics-loading">
        <h1 className="font-heading font-semibold text-4xl mb-1">Analytics</h1>
        {dateFilter}
        <p className="text-foreground/60">Caricamento analisi…</p>
      </div>
    );
  }
  if (!data?.totals) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-10 space-y-6" data-testid="pro-analytics-empty">
        <h1 className="font-heading font-semibold text-4xl mb-1">Analytics</h1>
        {dateFilter}
        <p className="text-foreground/60">Nessun dato disponibile per il periodo selezionato.</p>
        {user?.is_center && <CenterStaffAnalytics />}
      </div>
    );
  }

  const t = data.totals;
  const pi = data.premium_insight || {};

  return (
    <div className="max-w-6xl mx-auto px-4 py-10 space-y-6" data-testid="pro-analytics-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-semibold text-4xl mb-1">Analytics</h1>
          <p className="text-foreground/70">Richieste ricevute, guadagni e crediti spesi{data.date_from ? ` · ${data.date_from} – ${data.date_to}` : ''}</p>
        </div>
        {dateFilter}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Richieste ricevute" value={t.received} testid="pro-kpi-received" />
        <Kpi label="Accettate" value={t.accepted} sub={`${t.acceptance_rate}% di accettazione`} testid="pro-kpi-accepted" />
        <Kpi label="Completate" value={t.completed} testid="pro-kpi-completed" />
        <Kpi label="Prese da altri" value={t.lost_to_others} sub={`${t.pending} ancora aperte`} testid="pro-kpi-lost" />
      </div>

      <Card className={`rounded-2xl p-5 ${pi.is_premium ? 'border-amber-300/70 bg-amber-50/60' : 'border-primary/30 bg-primary/5'}`} data-testid="pro-premium-insight">
        <div className="flex items-start gap-3">
          <Crown size={26} weight="duotone" className={pi.is_premium ? 'text-amber-600' : 'text-primary'} />
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-heading font-medium">
                {pi.is_premium ? 'Il tuo vantaggio Premium' : 'Quanto potresti ricevere come Premium'}
              </p>
              {pi.is_premium && <Badge className="bg-amber-500 text-white">PREMIUM ATTIVO</Badge>}
            </div>
            <p className="text-sm text-foreground/75 mt-1">{pi.message}</p>
            <div className="grid sm:grid-cols-3 gap-4 mt-4">
              {pi.is_premium ? (
                <>
                  <Kpi label="Richieste in più grazie al Premium" value={`+${pi.leads_thanks_to_premium}`} testid="pro-premium-gain" />
                  <Kpi label="Senza Premium avresti" value={pi.leads_if_downgrade} sub="richieste nel periodo" testid="pro-premium-downgrade" />
                  <Kpi label="Priorità sulle segnalazioni" value={`${pi.uplift_factor}x`} testid="pro-premium-factor" />
                </>
              ) : (
                <>
                  <Kpi label="Stima con Premium" value={pi.estimated_leads_with_premium} sub={`oggi: ${t.received}`} testid="pro-premium-estimate" />
                  <Kpi label="Richieste in più" value={`+${pi.extra_leads_with_premium}`} testid="pro-premium-extra" />
                  <Kpi label="Guadagni potenziali in più" value={`€${pi.estimated_extra_earnings_eur}`} testid="pro-premium-earnings" />
                </>
              )}
            </div>
            {!pi.is_premium && (
              <Link to="/premium">
                <Button className="rounded-full mt-4" data-testid="pro-go-premium-btn">
                  <Crown size={16} className="mr-1.5" /> Passa a Premium
                </Button>
              </Link>
            )}
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="rounded-2xl p-5" data-testid="pro-earnings-card">
          <div className="flex items-center gap-2 mb-3">
            <TrendUp size={20} className="text-emerald-600" weight="duotone" />
            <p className="font-heading font-medium">Guadagni</p>
          </div>
          <p className="text-3xl font-heading font-bold">€{t.earnings_eur}</p>
          <p className="text-xs text-foreground/60 mt-1">Media per incarico accettato: €{t.avg_earning_per_accepted}</p>
        </Card>
        <Card className="rounded-2xl p-5" data-testid="pro-credits-card">
          <div className="flex items-center gap-2 mb-3">
            <Coins size={20} className="text-amber-600" weight="duotone" />
            <p className="font-heading font-medium">Crediti spesi</p>
          </div>
          <p className="text-3xl font-heading font-bold">{t.credits_spent}</p>
          <p className="text-xs text-foreground/60 mt-1">Valore stimato: €{t.credits_cost_eur}</p>
        </Card>
        <Card className="rounded-2xl p-5" data-testid="pro-net-card">
          <div className="flex items-center gap-2 mb-3">
            <ChartLineUp size={20} className="text-primary" weight="duotone" />
            <p className="font-heading font-medium">Margine netto</p>
          </div>
          <p className={`text-3xl font-heading font-bold ${t.net_eur >= 0 ? 'text-emerald-700' : 'text-destructive'}`}>€{t.net_eur}</p>
          <p className="text-xs text-foreground/60 mt-1">Guadagni meno costo dei crediti</p>
        </Card>
      </div>

      <Card className="rounded-2xl p-5" data-testid="pro-series-card">
        <p className="font-heading font-medium mb-4">Andamento richieste</p>
        {data.series.length === 0 ? (
          <p className="text-sm text-foreground/60">Nessuna richiesta nel periodo selezionato.</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E3F2FD" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#5B7A96' }} />
              <YAxis tick={{ fontSize: 11, fill: '#5B7A96' }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E3F2FD' }} />
              <Legend />
              <Bar dataKey="received" name="Ricevute" fill="#2CA9E1" radius={[6, 6, 0, 0]} />
              <Bar dataKey="accepted" name="Accettate" fill="#1565C0" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="rounded-2xl p-5" data-testid="pro-types-card">
          <p className="font-heading font-medium mb-3">Tipologie di richieste ricevute</p>
          {data.top_types.length === 0 ? (
            <p className="text-sm text-foreground/60">Nessun dato.</p>
          ) : (
            <div className="space-y-1.5">
              {data.top_types.map(x => (
                <div key={x.name} className="flex items-center justify-between border border-border rounded-lg px-3 py-2">
                  <span className="text-sm truncate">{x.name}</span>
                  <span className="text-sm font-medium text-primary shrink-0">{x.count}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card className="rounded-2xl p-5" data-testid="pro-zones-card">
          <p className="font-heading font-medium mb-3">Zone di provenienza</p>
          {data.top_zones.length === 0 ? (
            <p className="text-sm text-foreground/60">Nessun dato.</p>
          ) : (
            <div className="space-y-1.5">
              {data.top_zones.map(x => (
                <div key={x.name} className="flex items-center justify-between border border-border rounded-lg px-3 py-2">
                  <span className="text-sm truncate">{x.name}</span>
                  <span className="text-sm font-medium text-primary shrink-0">{x.count}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {user?.is_center && <CenterStaffAnalytics />}
    </div>
  );
}
